/**
 * Rekenmotor van de energiecalculator (flowchart 5 oktober, PLAN.md §5.4).
 *
 * Vier instellingen in, twee kolommen uit: lithium-ion naast loodzuur. De
 * motor is bewust transparant en dom: elke coëfficiënt staat hieronder als
 * benoemd getal in `AANNAMES`, en nergens anders. De echte waarden komen van
 * Product (§8: 17 oktober); tot die tijd zijn dit **indicatieve gemiddelden**
 * voor een elektrische magazijntruck, en zegt de UI dat erbij.
 *
 * Geen afrondingen hier: de UI formatteert via `Intl.NumberFormat` (§5.4).
 */

export interface EnergieInvoer {
  /** Aantal trucks in de vloot. */
  trucks: number
  /** Shifts per dag. */
  shifts: number
  /** Draaiuren per truck per dag (over alle shifts samen). */
  uren: number
  /** Uren per dag waarin er geladen kan worden (pauzes, nacht). */
  laadtijd: number
}

export interface EnergieUitkomst {
  /** Batterijen per truck. Loodzuur heeft bij meerploegendienst wisselbatterijen nodig. */
  batterijenPerTruck: number
  /** Batterijwissels per dag, hele vloot. Lithium-ion: altijd 0. */
  wisselsPerDag: number
  /** Netstroom uit de muur per dag, hele vloot, in kWh (verbruik gedeeld door laadrendement). */
  kWhPerDag: number
  /** Piekvermogen als de hele vloot tegelijk laadt, in kW. */
  piekKw: number
  /** Of de benodigde laadtijd in de beschikbare laadtijd past. */
  pastInLaadtijd: boolean
}

export interface EnergieVergelijking {
  lithium: EnergieUitkomst
  loodzuur: EnergieUitkomst
  /** Hoeveel procent minder netstroom lithium-ion nodig heeft dan loodzuur. */
  besparingProcent: number
}

/** Grenzen en stapgrootte per instelling, voor de stapknoppen (§5.4: geen sliders). */
export const BEREIK: Record<keyof EnergieInvoer, { min: number; max: number; stap: number }> = {
  trucks: { min: 1, max: 60, stap: 1 },
  shifts: { min: 1, max: 3, stap: 1 },
  uren: { min: 1, max: 20, stap: 1 },
  laadtijd: { min: 1, max: 16, stap: 1 },
}

export const STANDAARD_INVOER: EnergieInvoer = { trucks: 10, shifts: 2, uren: 12, laadtijd: 6 }

/**
 * AANNAMES — in te vullen door Product. Elk getal hier is een gemiddelde voor
 * een elektrische magazijntruck van de 1,6–2,0 t-klasse en is **niet**
 * gevalideerd. De namen zijn de documentatie.
 */
export const AANNAMES = {
  /** Gemiddeld opgenomen vermogen tijdens het werk, kW (energie uit de batterij per draaiuur). */
  verbruikKwPerUur: 3.2,
  /** Bruikbare batterijcapaciteit per truck, kWh. */
  capaciteitKwh: { lithium: 24, loodzuur: 24 },
  /** Hoeveel van de capaciteit per lading echt gebruikt wordt (loodzuur: niet onder 20%). */
  bruikbaarDeel: { lithium: 0.9, loodzuur: 0.8 },
  /** Rendement van laden: wat uit de muur komt tegenover wat in de batterij belandt. */
  laadrendement: { lithium: 0.92, loodzuur: 0.78 },
  /** Laadvermogen per lader, kW. Lithium-ion laadt snel en tussendoor; loodzuur traag. */
  laadvermogenKw: { lithium: 12, loodzuur: 5 },
  /**
   * Loodzuur moet na een volledige lading afkoelen voordat hij weer in
   * kan — uren per cyclus die niet aan werken of laden besteed kunnen worden.
   */
  afkoeltijdUur: { lithium: 0, loodzuur: 2 },
}

const TECHNIEKEN = ['lithium', 'loodzuur'] as const
type Techniek = (typeof TECHNIEKEN)[number]

function bereken(invoer: EnergieInvoer, t: Techniek): EnergieUitkomst {
  const a = AANNAMES
  const perTruckKwh = invoer.uren * a.verbruikKwPerUur
  const perLadingKwh = a.capaciteitKwh[t] * a.bruikbaarDeel[t]
  const ladingenPerDag = perTruckKwh / perLadingKwh
  const laadtijdPerLadingUur = perLadingKwh / a.laadvermogenKw[t] + a.afkoeltijdUur[t]

  // Lithium-ion laadt tussendoor (opportunity charging): één batterij, mits de
  // laadtijd past. Loodzuur kan dat niet; wat niet in één lading past, vraagt
  // een wisselbatterij die ondertussen aan de lader en in de afkoeling ligt.
  const batterijenPerTruck = t === 'lithium' ? 1 : Math.max(1, Math.ceil(ladingenPerDag))
  const wisselsPerDag = t === 'lithium' ? 0 : Math.max(0, batterijenPerTruck - 1) * invoer.trucks

  const netKwhPerTruck = perTruckKwh / a.laadrendement[t]
  const benodigdeLaadtijdUur = ladingenPerDag * laadtijdPerLadingUur
  // Met wisselbatterijen laadt loodzuur parallel aan het werk; zonder moet alles in de laadtijd.
  const pastInLaadtijd = t === 'lithium'
    ? benodigdeLaadtijdUur <= invoer.laadtijd
    : benodigdeLaadtijdUur <= invoer.laadtijd + (batterijenPerTruck - 1) * invoer.uren

  return {
    batterijenPerTruck,
    wisselsPerDag,
    kWhPerDag: netKwhPerTruck * invoer.trucks,
    piekKw: a.laadvermogenKw[t] * invoer.trucks,
    pastInLaadtijd,
  }
}

export function vergelijk(invoer: EnergieInvoer): EnergieVergelijking {
  const lithium = bereken(invoer, 'lithium')
  const loodzuur = bereken(invoer, 'loodzuur')
  const besparingProcent = loodzuur.kWhPerDag > 0 ? (1 - lithium.kWhPerDag / loodzuur.kWhPerDag) * 100 : 0
  return { lithium, loodzuur, besparingProcent }
}

/** Eén stap omhoog of omlaag, binnen het bereik. */
export function stap(invoer: EnergieInvoer, veld: keyof EnergieInvoer, richting: -1 | 1): number {
  const b = BEREIK[veld]
  return Math.min(b.max, Math.max(b.min, invoer[veld] + richting * b.stap))
}
